import { useRouterState } from "@tanstack/react-router";
import { useRegisterSW } from "virtual:pwa-register/react";
import { WifiOff } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useT } from "~/lib/i18n";
import { Button } from "../ui/button";

export function OfflineBanner() {
  const t = useT();
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);
  if (online) return null;
  return (
    <output className="flex items-center gap-2 bg-warning-soft px-4 py-2 text-sm text-warning">
      <WifiOff className="size-4 shrink-0" aria-hidden />
      {t("errors.offline")}
    </output>
  );
}

/** How often an open app looks for a new deploy (a browser only does it on navigations, or daily). */
const UPDATE_CHECK_MS = 15 * 60_000;

/**
 * A new deploy never reloads under the user's feet: they can update from the prompt, and otherwise
 * the new version takes over at their next change of screen, when nothing is being typed. An app
 * left open (a phone's home-screen app in the background) looks for new versions by itself, so it
 * does not keep running an old screen against a newer API.
 */
export function PwaUpdatePrompt() {
  const t = useT();
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    immediate: true,
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      const check = () => {
        if (document.visibilityState === "visible" && navigator.onLine) void registration.update().catch(() => undefined);
      };
      window.setInterval(check, UPDATE_CHECK_MS);
      document.addEventListener("visibilitychange", check);
    },
  });
  const [updating, setUpdating] = useState(false);
  const started = useRef(false);
  const update = useCallback(async () => {
    if (started.current) return;
    started.current = true;
    setUpdating(true);
    // The plugin's own path (message to the waiting worker, reload once it controls the page)
    // has been seen doing nothing: the waiting worker is told directly too, and the page reloads
    // as soon as it takes over, or after a short while whatever happens.
    navigator.serviceWorker?.addEventListener("controllerchange", () => window.location.reload(), { once: true });
    const registration = await navigator.serviceWorker?.getRegistration();
    registration?.waiting?.postMessage({ type: "SKIP_WAITING" });
    void updateServiceWorker(true);
    window.setTimeout(() => window.location.reload(), 3000);
  }, [updateServiceWorker]);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // The screen shown when the new version was found: leaving it applies the update.
  const shownOn = useRef<string | null>(null);
  useEffect(() => {
    if (!needRefresh) {
      shownOn.current = null;
      return;
    }
    if (shownOn.current === null) shownOn.current = pathname;
    else if (shownOn.current !== pathname) void update();
  }, [needRefresh, pathname, update]);
  if (!needRefresh) return null;
  return (
    <output className="fixed right-4 bottom-[calc(5rem+env(safe-area-inset-bottom))] left-4 z-50 flex items-center gap-3 rounded-panel bg-ink px-4 py-3 text-sm text-white shadow-pop md:bottom-5 md:left-auto md:max-w-sm">
      <span className="flex-1">{t("pwa.updateAvailable")}</span>
      <Button variant="primary" size="sm" loading={updating} onClick={() => void update()}>
        {t("pwa.updateAction")}
      </Button>
    </output>
  );
}

/**
 * A screen that crashed may be an old version of the app talking to a newer API (it stayed cached
 * by the service worker, and its update prompt lives in the shell the crash replaced): if a newer
 * version is available, it takes over and the page reloads — at most once a minute.
 */
export function useNewVersionOnCrash() {
  useEffect(() => {
    const sw = typeof navigator !== "undefined" ? navigator.serviceWorker : undefined;
    if (!sw) return;
    let last = 0;
    try {
      last = Number(sessionStorage.getItem("erp.crashReload") ?? 0);
    } catch {
      // Storage may be blocked: the reload guard just starts from zero.
    }
    if (Date.now() - last < 60_000) return;
    void (async () => {
      const registration = await sw.getRegistration();
      if (!registration) return;
      await registration.update().catch(() => undefined);
      const next = registration.waiting ?? registration.installing;
      if (!next) return;
      try {
        sessionStorage.setItem("erp.crashReload", String(Date.now()));
      } catch {
        // See above.
      }
      sw.addEventListener("controllerchange", () => window.location.reload(), { once: true });
      next.postMessage({ type: "SKIP_WAITING" });
      window.setTimeout(() => window.location.reload(), 4000);
    })();
  }, []);
}
